// TulyFlow.revised.jsx
// Revised with UX improvements: split workflows + timeline, scannable timeline entries,
// prominent templates carousel (Use Template / Preview), enriched "What you get" outcomes panel,
// and improved hero/search with suggestions, progress integration and microinteractions.

import React, { useEffect, useState, useRef, Fragment } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Clock, Sparkles,ChevronRight, Search, FileX2, ChevronLeft, PanelRightClose, PanelRightOpen, MoreHorizontal, Star, Archive, Copy, Play, Eye, Share2 } from 'lucide-react';

// existing API helpers from your codebase (kept same names for integration)
import { getTulyFlow, allThreads, appendSearchToThread, createAThread, getThreadById, getExecutiveReport, removeSearch } from '../api/api';

import TaskSearchBar from '../components/TaskSearchBar';
import FlowStickyNavbar from '../components/FlowStickyNavbar';
import AIReportDashboard from '../components/AIReportDashboard';
import AIWorkflowVisualizer from '../components/AIWorkflowVisualizer';
import WorkflowError from '../components/WorkflowError';
import { isReportData, sanitizeReportData } from '../utils/reportUtils';
import { useHistory } from '../hooks/useHistory';
import { useAuth } from '../utils/AuthContext';
import ShareModal from '../components/ShareModal';
import WorkflowLimitBlock from '../components/WorkflowLimitBlock'

import { useLocation } from "react-router-dom";

import { ToastContainer, toast } from 'react-toastify';

// --- Utility components inside file for clarity (move to separate files as needed) ---
const IconOnlyCollapsed = ({ onExpand }) => (
  <div className="w-20 bg-white rounded-r-lg shadow-lg p-2 sticky top-24">
    <div className="flex flex-col items-center gap-3">
      <button title="Expand workflows" onClick={onExpand} className="p-2 rounded hover:bg-gray-100">
        <ChevronRight size={18} />
      </button>
      <button title="New workflow" className="p-2 rounded hover:bg-gray-100">+</button>
    </div>
  </div>
);

const ExpandableText = ({ text, maxLines = 3 }) => {
  const [expanded, setExpanded] = useState(false);
  const lines = text.split("\n");

  if (lines.length <= maxLines) {
    return (
      <div className="text-sm font-medium break-words whitespace-pre-wrap">
        {text}
      </div>
    );
  }

  return (
    <div>
      <div className="text-sm font-medium break-words whitespace-pre-wrap">
        {expanded ? text : lines.slice(0, maxLines).join("\n")}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-blue-600 text-xs font-medium mt-1 hover:underline"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
};

const WorkflowRow = ({ t, selected, onSelect, onPinToggle, onMenu }) => (
  <div
    onClick={() => onSelect(t)}
    className={`p-2 rounded cursor-pointer flex items-center justify-between gap-3 ${selected ? 'bg-indigo-50 border border-indigo-100' : 'hover:bg-gray-50'}`}>
    <div className="flex items-start gap-3">
      <button onClick={(e) => { e.stopPropagation(); onPinToggle(t); }} className="text-yellow-500 p-1 rounded hover:bg-gray-100" title="Pin">
        <Star size={16} />
      </button>
      <div>
        <div className="text-sm font-medium truncate max-w-[180px]">{t.threadName}</div>
        <div className="text-xs text-gray-400">{(t.searches?.length || 0)} searches • Last used {t.lastUsed ? new Date(t.lastUsed).toLocaleDateString() : new Date(t.createdAt).toLocaleDateString()}</div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button onClick={(e) => { e.stopPropagation(); onMenu(t); }} className="p-1 rounded hover:bg-gray-100" title="More">
        <MoreHorizontal size={16} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onSelect(t); }} className="p-1 rounded hover:bg-gray-100" title="Open timeline">
        <ChevronRight size={16} />
      </button>
    </div>
  </div>
);

const TimelineEntry = ({
  entry,
  enableRun,
  onReRun,
  onViewReport,
  onShareReport,
  onRemove,
  onCopy,
}) => {
  const typeBadge = entry.type || (entry.response ? "Report" : "Query");
  const confidence = typeof entry.confidence === "number" ? entry.confidence : null;

  return (
    <motion.div
      whileHover={{ y: -2, scale: 1.01 }}
      className="relative border border-gray-100 rounded-2xl bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-sm hover:shadow-md transition-all p-5"
    >
      {/* Glow Accent */}
      <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400" />

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium shadow-sm">
            {typeBadge}
          </span>
          {confidence !== null && (
            <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium shadow-sm">
              AI {confidence}%
            </span>
          )}
        </div>
        <div className="text-xs text-gray-400 font-medium">
          {new Date(entry.createdAt).toLocaleDateString()}
        </div>
      </div>

      {/* Query Content */}
      <div className="text-sm text-gray-800 leading-relaxed relative">
        <ExpandableText text={entry.query} />
      </div>

      {/* Divider */}
      <div className="my-4 border-t border-gray-100"></div>

      {/* Footer Actions */}
      <div className="flex flex-wrap gap-2 text-xs font-medium">
        <ActionButton
          label="View"
          icon={<Eye size={13} />}
          color="indigo"
          onClick={() => onViewReport(entry.response)}
        />
        <ActionButton
          label="Share"
          icon={<Share2 size={13} />}
          color="purple"
          onClick={() => onShareReport(entry.searchId)}
          highlight
        />
        {enableRun && (
          <ActionButton
            label="Re-run"
            icon={<Play size={13} />}
            color="blue"
            onClick={() => onReRun(entry.query)}
          />
        )}
        <ActionButton
          label="Copy Prompt"
          icon={<Copy size={13} />}
          color="gray"
          onClick={() => onCopy(entry.query)}
        />
        <ActionButton
          label="Remove"
          icon={<FileX2 size={13} />}
          color="red"
          onClick={() => onRemove(entry.searchId)}
        />
      </div>
    </motion.div>
  );
};

const ActionButton = ({ label, icon, color, onClick, highlight = false }) => {
  const colorMap = {
    indigo: "bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 border-indigo-800",
    purple: "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 border-purple-800",
    blue: "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 border-blue-800",
    gray: "bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 border-gray-700",
    red: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 border-red-700",
  };
  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all text-white ${colorMap[color]} ${
        highlight ? "shadow-sm ring-1 ring-purple-100" : ""
      }`}
    >
      {icon}
      <span>{label}</span>
    </motion.button>
  );
};


// Utility: group entries by date bucket
const groupByDate = (entries) => {
  const now = new Date();
  const today = now.toDateString();

  return entries.reduce((groups, entry) => {
    const entryDate = new Date(entry.createdAt);
    const entryDay = entryDate.toDateString();

    let bucket = "Earlier";
    if (entryDay === today) {
      bucket = "Today";
    } else if ((now - entryDate) / (1000 * 60 * 60 * 24) <= 7) {
      bucket = "This Week";
    }

    if (!groups[bucket]) groups[bucket] = [];
    groups[bucket].push(entry);
    return groups;
  }, {});
};

const TemplateCard = ({ t, onUse, onPreview }) => (
  <div className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-lg transition-transform transform hover:-translate-y-1 flex flex-col h-full">
    <div className="flex items-start gap-4">
      <div className="text-3xl">{t.emoji}</div>
      <div className="flex-1">
        <div className="font-semibold">{t.title}</div>
        <div className="text-xs text-gray-500 mt-1">{t.desc}</div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded">{t.estTime}</span>
          <span className="text-xs bg-gray-50 text-gray-700 px-2 py-1 rounded">{t.estCost}</span>
          <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">Confidence: {t.confidence}%</span>
        </div>
      </div>
    </div>

    <div className="mt-4">
      <div className="flex items-center gap-2 flex-wrap">
        {t.tags.map(tag => (<span key={tag} className="text-xs px-2 py-1 bg-gray-100 rounded">{tag}</span>))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={() => onPreview(t)} className="text-xs px-3 py-1 border rounded">Preview</button>
        <button onClick={() => onUse(t)} className="text-xs px-3 py-1 bg-indigo-600 text-white rounded flex items-center gap-2"><Play size={14}/>Use Template</button>
      </div>
    </div>
  </div>
);

// --- Main component ---
export default function TulyFlow() {
  const { user } = useAuth();
  const token = Cookies.get('authToken');
  const { historyList, isLoading: historyLoading, refreshHistory } = useHistory();

  // global UI state
  const [threads, setThreads] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [currentThreadName, setCurrentThreadName] = useState('Default');

  const [collapsed, setCollapsed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const loadingRef = useRef(null);

  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineEntries, setTimelineEntries] = useState([]);
  const [filters, setFilters] = useState({ q: '', tag: 'all', dateRange: '30' });
  const [collapsedIcons, setCollapsedIcons] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(null);

  const [isShareOpen, setIsShareOpen] = useState(false);

  const [moveTrash, setMoveTrash] = useState(false);
  const [moveTrashSearchId, setMoveTrashSearchId] = useState('');

  const [showError, setShowError] = useState(false);

  const [isSearchEnabled, setIsShearchEnabled] = useState(true);
  const [currentSearches, setCurrentSearches] = useState(0);

  const [timelineFilter, setTimelineFilter] = useState('');
  const [searchId, setSearchId] = useState('');

  const location = useLocation();
  
  const { threadId, threadName, searchlimit } = location.state || {}; // Get passed value

  // fetch threads on mount
  useEffect(() => { 
    if(threadId){
      openThread();
    }
  }, []);

  function openThread() {
    setCurrentThreadId(threadId);
    setCurrentThreadName(threadName);
    setShowTimeline(true);
    loadTimeline(threadId);
  }

  async function loadTimeline(threadId) {
    try {
      setLoadingTimeline(true);
      const data = await getThreadById(token, threadId);
      const searches = data?.searches?.filter(s => s.isActive === true) // Only include active searches
        ?.map(s => ({ 
          ...s, 
          type: s.type || (s.response ? 'Report' : 'Query'), 
          confidence: s.confidence 
        })) || [];
      
      setTimelineEntries(searches.reverse()); // show latest first
      
      // Use the actual length directly instead of relying on state
      const searchCount = data.searches.length;
      setCurrentSearches(searchCount);
      
      console.log(searchCount); // This will show the correct value
      
      if(searchCount >= searchlimit){
        console.log('You have reached the limit!');
        setIsShearchEnabled(false);
      } else {
        console.log('You still have searches left to do!');
        setIsShearchEnabled(true);
      }
    } catch (e) { 
      console.error('loadTimeline', e); 
      setLoadingTimeline(false);
    } finally {
      setLoadingTimeline(false);
    }
  }

  // Loading steps animation
  function startLoadingSteps() {
    setLoadingStep(0);
    if (loadingRef.current) clearInterval(loadingRef.current);
    loadingRef.current = setInterval(() => {
      setLoadingStep(prev => Math.min(prev+1, 3));
    }, 700);
  }

  function stopLoadingSteps() { if (loadingRef.current) clearInterval(loadingRef.current); setLoadingStep(0); }

  // --- Search handler ---
  async function handleSearch(query, opts = {}) {
    try {
      setLoading(true); setSearchResults(null); startLoadingSteps();
      // call backend flow
      const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const results = await getTulyFlow(query, token, today);
      

      // log search into current thread
      try {
        await createAThread(token, currentThreadId || uuidv4(), currentThreadName || 'Default', query, results);
        openThread();
      } catch (logErr) { console.warn('log failed', logErr); }

      const parsed = cleanJSONResponse(results);
      if (isReportData(parsed)) {
        const sanitized = sanitizeReportData(parsed);
        setSearchResults(sanitized);
        window.dispatchEvent(new CustomEvent('tuly-report-generated', { detail: sanitized }));
      } else {
        setSearchResults(parsed);
      }

    } catch (err) {
      console.error('search error', err);
    } finally {
      setLoading(false); stopLoadingSteps();
    }
  }

  const filteredTimelineEntries = timelineEntries.filter(entry =>
    entry.query.toLowerCase().includes(timelineFilter.toLowerCase())
  );

  // timeline action handlers
  function onReRun(query) { 
    handleSearch(query); 
  }

  function onViewReport(response) { 
    try { 
      const parsed = cleanJSONResponse(response);
      const sanitized = isReportData(parsed) ? sanitizeReportData(parsed) : parsed; 
      setSearchResults(sanitized); 
    } catch (e) { 
      setSearchResults(null);
      setLoading(false);
      setShowError(true);
      console.error(e);
    } 
  }

  function shareReport(searchId) { 
    try { 
      setSearchId(searchId);
      setIsShareOpen(true);
    } catch (e) { 
      
    } 
  }

  function cleanJSONResponse(response) {
    if (!response) return null;
    
    // Remove triple backticks and optional "json" label
    let cleaned = response.trim()
      .replace(/^```json\s*/i, '') // remove starting ```json
      .replace(/^```\s*/i, '')     // remove starting ```
      .replace(/\s*```$/i, '');    // remove ending ```
    
    // Fix numeric range values (like "300-700" -> "300")
    // This regex finds patterns like "field": 300-700 and converts to "field": "300-700"
    cleaned = cleaned.replace(/("(?:monthly_cost|annual_cost|cost|price|budget)":\s*)(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/g, '$1"$2-$3"');
    
    try {
      return JSON.parse(cleaned);
    } catch (err) {
      console.error("Invalid JSON after cleaning:", err);
      console.error("Cleaned content:", cleaned);
      return null;
    }
  }

  async function onExecReport(response) { 
    try { 
      console.log(response);

      const formatedResponse = JSON.stringify(reformatApi(response));
      
      const data = await getExecutiveReport(formatedResponse, token);

      console.log('> '+data);

    } catch (e) { 
      console.error(e); 
    } 
  }

  // Function to process API response and extract required keys
  function reformatApi(apiResponse) {
    try {
      // Parse the API response if it's a string, otherwise use as-is
      const data = typeof apiResponse === 'string' ? JSON.parse(apiResponse) : apiResponse;
      
      // Extract the required keys
      const newJsonObject = {
        implementation_strategy: data.implementation_strategy,
        ai_tools_selection: data.ai_tools_selection
      };
      
      return newJsonObject;
    } catch (error) {
      console.error('Error processing API response:', error);
      return null;
    }
  }

  async function prepareToMoveSearch(){
    const response = await removeSearch(token, currentThreadId, moveTrashSearchId);
    if(response.responsecode == 200){
      setMoveTrash(false);
      loadTimeline(threadId);
      toast("Search record removed!");
    } else {
      toast("Error while removeing search reocrd!");
    }
  }

  function onRemove(searchId){
    setMoveTrashSearchId(searchId);
    setMoveTrash(true);
  }

  function onCopy(query) { 
    navigator.clipboard?.writeText(query); 
    toast("Prompt copied to clipboard!");
  }

  // UI helpers for hero suggestions
  const suggestionChips = ['Include budget estimate', 'Add risk assessment', 'Target: e‑commerce', 'Deliverable: executive summary'];

  // What-you-get sample dynamic calculator (rudimentary)
  function estimateCostFromConfidence(conf) {
    if (!conf) return '$1k–$3k';
    if (conf > 85) return '$1k–$2k';
    if (conf > 70) return '$1.5k–$3k';
    return '$2k–$6k';
  }

  const groupedEntries = groupByDate(filteredTimelineEntries);

  return (
    <div className="min-h-screen bg-gray-50">
      
      <ToastContainer
        position="top-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"/>

      <FlowStickyNavbar threadName={currentThreadName}/>

      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        threadId={threadId}
        searchId={searchId} />

      {/* Modal Overlay */}
      {moveTrash && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {/* Modal Content */}
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="p-6 pb-4">
              <h4 className="text-xl font-semibold text-gray-900">
                Trash report?
              </h4>
            </div>

            {/* Modal Body */}
            <div className="px-6 pb-4">
              <p className="text-gray-900 mb-4">
                This search's report will be sent to trash!
              </p>
              <p className="text-gray-500 text-sm">
                Don't worry, Workflow report will be accessible through generated share links for your friends! 
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 pt-4">
              <button
                onClick={() => {setMoveTrash(false);} }
                className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={prepareToMoveSearch}
                className="px-6 py-2 bg-red-500 text-white hover:bg-red-600 transition-colors">
                Trash
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex pt-16 h-screen">

        {/* LEFT: timeline stacked */}
<div className={`${
    collapsed ? "w-16" : "w-96"
  } transition-all duration-300 border-r bg-white flex flex-col`}>
  {collapsed ? (
    // Collapsed state
    <div className="p-4 flex flex-col items-center gap-4">
      <button
        onClick={() => setCollapsed(false)}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        title="Expand timeline"
      >
        <PanelRightClose color="#0056d6" strokeWidth={1} size={30} />
      </button>
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold shadow-lg">
        {timelineEntries.length}
      </div>
    </div>
  ) : (
    // Expanded
    <>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b">
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-bold text-gray-900 text-lg">Timeline</div>
                <div className="text-xs text-gray-500">
                  {filteredTimelineEntries.length} {filteredTimelineEntries.length === 1 ? 'entry' : 'entries'}
                </div>
              </div>
            </div>
            <button
              onClick={() => setCollapsed(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 group"
              title="Collapse timeline"
            >
              <PanelRightOpen 
                className="text-gray-400 group-hover:text-gray-600 transition-colors" 
                strokeWidth={1.5} 
                size={20} 
              />
            </button>
          </div>
          
          {/* Search Box */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search timeline..."
              value={timelineFilter}
              onChange={(e) => setTimelineFilter(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white transition-all duration-200 placeholder:text-gray-400"
            />
            {timelineFilter && (
              <button
                onClick={() => setTimelineFilter("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center group"
              >
                <div className="w-5 h-5 rounded-full bg-gray-200 group-hover:bg-gray-300 flex items-center justify-center transition-colors">
                  <X className="h-3 w-3 text-gray-600" />
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Timeline Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-8 relative bg-gradient-to-b from-gray-50/50 to-white">
        {/* Vertical line */}
        <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-200 via-purple-200 to-transparent"></div>
        
        {loadingTimeline && 
          <div className="space-y-6 animate-pulse">
            {[1,2,3].map(i => (
              <div key={i} className="relative pl-12">
                <span className="absolute left-7 top-4 w-3 h-3 rounded-full bg-gray-300 border-2 border-white shadow-sm"></span>
                <div className="h-6 bg-gray-200 rounded-lg w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-100 rounded-lg w-1/3"></div>
              </div>
            ))}
          </div>
        }
        
        {!loadingTimeline && 
        <>
          <AnimatePresence>
            {Object.keys(groupedEntries).map((bucket, bucketIndex) => (
              <div key={bucket} className="relative">
                {/* Section label */}
                <div className="flex items-center gap-2 mb-4 sticky top-0 bg-white/80 backdrop-blur-sm py-2 z-10">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2">
                    {bucket}
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                </div>
                
                <div className="space-y-6">
                  {groupedEntries[bucket].map((entry, entryIndex) => (
                    <div key={entry.id} className="relative pl-12">
                      {/* Dot on the timeline with gradient glow */}
                      <div className="absolute left-[26px] top-4 w-4 h-4 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 border-2 border-white shadow-lg ring-2 ring-indigo-100"></div>
                      <TimelineEntry
                        entry={entry}
                        onReRun={onReRun}
                        onViewReport={onViewReport}
                        onShareReport={shareReport}
                        onCopy={onCopy}
                        onRemove={onRemove}
                        enableRun={isSearchEnabled}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </AnimatePresence>
          
          {filteredTimelineEntries.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-12 px-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-gray-400" /> <Clock />
              </div>
              <div className="text-sm font-medium text-gray-900 mb-1">No timeline entries yet</div>
              <div className="text-xs text-gray-500">Create your first workflow report to get started</div>
            </div>
          )}
        </>
        }
      </div>
    </>
  )}
</div>

        {/* MAIN: hero, search, templates, what-you-get, results */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 max-w-6xl mx-auto">
            {isSearchEnabled &&
              <div className="bg-gradient-to-r bg-custom-gradient rounded-2xl p-6 shadow-xl text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex-1">
                    <h1 className="text-3xl md:text-4xl font-extrabold">Turn your idea into an AI-driven workflow</h1>
                    <p className="mt-2 text-indigo-100 max-w-xl">Describe the goal — Tuly will return a practical, implementable plan with tools, budget, risks and an executive summary.</p>

                    <div className="mt-4">
                      
                      <TaskSearchBar onSearch={handleSearch} placeholder="e.g. Grow online sales for new serum — include budget & timeline" suggestions={suggestionChips} isLoading={loading}/>
                      
                      <div className="mt-2 flex items-center gap-2 text-xs text-indigo-100">
                        {suggestionChips.map(s => (<span key={s} className="bg-white/10 px-2 py-1 rounded">{s}</span>))}
                      </div>
                    </div>
                  </div>

                  <div className="hidden md:block w-72">
                    {/* 'What you get' interactive preview */}
                    <div className="bg-white text-gray-800 rounded-lg p-4 shadow-md">
                      <div className="font-semibold">What you get</div>
                      <div className="mt-2 text-sm">
                        <div className="flex items-start gap-2">
                          <div className="w-3 h-3 rounded-full bg-indigo-900 mt-1" />
                          <div>Actionable workflow (step-by-step)</div>
                        </div>
                        <div className="flex items-start gap-2 mt-2">
                          <div className="w-3 h-3 rounded-full bg-indigo-900 mt-1" />
                          <div>Tools & integrations with alternatives</div>
                        </div>
                        <div className="flex items-start gap-2 mt-2">
                          <div className="w-3 h-3 rounded-full bg-indigo-900 mt-1" />
                          <div>Budget, risks, & ROI simulation</div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-gray-500">• Trusted by teams</div>
                    </div>
                  </div>

                </div>
              </div>
            }

            {!isSearchEnabled &&
            <div className="flex justify-center">
              <WorkflowLimitBlock
                currentCount={currentSearches}
                maxLimit={searchlimit}
              />
            </div>
            }

            {/* Loading steps */}
            <div className="mt-6">
              <AnimatePresence>
                {loading && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <div className="bg-white rounded-xl shadow p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">Generating workflow</div>
                        <div className="text-xs text-gray-500">Step {loadingStep + 1} of 4</div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-indigo-500 h-2 rounded-full transition-all duration-300 ease-out"
                            style={{ width: `${((loadingStep + 1) / 4) * 100}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 mt-1 text-center">
                          {Math.round(((loadingStep + 1) / 4) * 100)}% complete
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {['Analyzing request', 'Selecting tools', 'Estimating budget', 'Preparing report'].map((s, i) => (
                          <div key={s} className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              i < loadingStep ? 'bg-green-500' : 
                              i === loadingStep ? 'bg-indigo-500 animate-pulse' : 
                              'bg-gray-300'
                            }`} />
                            <div className={`${
                              i < loadingStep ? 'text-green-700' : 
                              i === loadingStep ? 'text-indigo-700 font-medium' : 
                              'text-gray-400'
                            } text-sm flex items-center`}>
                              {s}
                              {i === loadingStep && i < 3 && <span className="ml-1">...</span>}
                              {i === loadingStep && i === 3 && (
                                <div className="ml-2 flex items-center gap-2">
                                  <div className="flex space-x-1">
                                    <div className="w-1 h-1 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                                    <div className="w-1 h-1 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                                    <div className="w-1 h-1 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                                  </div>
                                  <span className="text-xs text-indigo-600 font-normal">This may take a moment</span>
                                </div>
                              )}
                              {i < loadingStep && <span className="ml-2 text-green-600">✓</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Additional loading indicator for final step */}
                      {loadingStep === 3 && (
                        <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                          <div className="flex items-center gap-2 text-indigo-700">
                            <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin"></div>
                            <span className="text-sm">Finalizing your workflow report...</span>
                          </div>
                          <div className="text-xs text-indigo-600 mt-1 ml-6">
                            Compiling detailed analysis and recommendations
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Results area */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Templates (prominent) */}
              <div className="lg:col-span-3">

                {/* If searchResults present and isReportData -> show dashboard */}
                {!loading && searchResults && isReportData(searchResults) && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold">Generated Workflow</h3>
                      <div className="flex items-center ">
                        {/*<button onClick={() => setIsShareOpen(true)} className="px-3 py-2 text-black rounded flex items-center gap-2">
                          <Share2 color="#0061ff" strokeWidth={1.75} /> Share 
                        </button> */}
                      </div>
                    </div>
                    <AIReportDashboard reportData={searchResults} onBackToSearch={() => setSearchResults(null)} /> 
                  </div>
                )}

                {/* Non-report results */}
                {!loading && searchResults && !isReportData(searchResults) && (
                  <div className="mt-4 bg-white rounded-xl p-4 shadow">
                    <div className="p-8">
                      <AnimatePresence>
                        {showError && <WorkflowError onClose={() => setShowError(false)} />}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* Non-report results */}
                {!loading && !searchResults && showError &&(
                  <div className="mt-4 rounded-xl  ">
                    <div className="p-8">
                      <AnimatePresence>
                        {showError && <WorkflowError onClose={() => setShowError(false)} />}
                      </AnimatePresence>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #c7c7c7; border-radius: 6px; }
      `}</style>
    </div>
  );
}
